import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, Grid, Stack, Typography, styled, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "Components/_Shared/Buttons/Button";
import { ReactComponent as AddIcon } from "Components/_Shared/Icons/Assets/add.svg";
import { ReactComponent as ArrowDownIcon } from "Components/_Shared/Icons/Assets/arrow-down.svg";
import { ReactComponent as BinIcon } from "Components/_Shared/Icons/Assets/bin.svg";
import { TextInput } from "Components/_Shared/Inputs/TextInput";
import { Popper } from "Components/_Shared/Popper";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { DatePicker } from "Forms/Components/DatePicker";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { FormDropdown, type Option } from "Forms/Components/FormDropdown";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { type ImmovableContractFormType, getImmovableContractFormSchema } from "Forms/Schemas/Assets/immovableContractFormSchema";
import {
  getListImmovableAssetContractsQueryKey,
  useCreateImmovableAssetContract,
  useDeleteAssetContract,
  useGetImmovableAssetContract,
  useListAnalyzerPartners,
  useListAssetContractExpenseOptions,
  useUpdateImmovableAssetContract,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, ListAnalyzerPartnersToolType, type ProposedSolutionStep } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { LOAD_VALUES_EXPENSE_TYPE_RESOURCE_KEY } from "Infrastructure/Utils/AssetUtils";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { getAmountFrequencyTranslation } from "Infrastructure/Utils/LanguageUtils";
import React, { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "../constants";
import type { SubmitContractFormHandle } from "./ContractDrawer";

type Props = {
  readonly assetContractId?: string;
  readonly assetItemId: string;
  readonly onClose: () => void;
  readonly onPendingChange?: (pending: boolean) => void;
};

const calculateUnderinsurance = (current: number | undefined, recommended: number | undefined): number | undefined => {
  if (!recommended) return 0;

  if (current && recommended) {
    return (1 - current / recommended) * -100;
  }
};

const StyledItem = styled(Stack)`
  width: 300px;
  &:hover {
    cursor: pointer;
    background: rgba(0, 0, 0, 0.03);
  }
`;

export const ImmovableContractForm = forwardRef<SubmitContractFormHandle, Props>(
  ({ assetItemId, assetContractId, onClose, onPendingChange }, ref) => {
    const queryClient = useQueryClient();
    const theme = useTheme();
    const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);
    const { t } = useResource();

    const [expenseOptionsDropdownOpen, setExpenseOptionsDropdownOpen] = useState(false);

    const { data: insurancePartnersData } = useListAnalyzerPartners({ toolType: ListAnalyzerPartnersToolType.insurance });
    const { data } = useGetImmovableAssetContract(analysisId, versionId, assetItemId, assetContractId ?? "", {
      query: { enabled: !!assetContractId },
    });
    const { data: expenseOptions } = useListAssetContractExpenseOptions(analysisId, versionId);

    const { mutateAsync: createAsync, isPending: isCreating } = useCreateImmovableAssetContract();
    const { mutateAsync: editAsync, isPending: isEditing } = useUpdateImmovableAssetContract();
    const { mutateAsync: deleteAsync, isPending: isDeleting } = useDeleteAssetContract();

    const isMutating = isCreating || isEditing || isDeleting;

    useEffect(() => {
      onPendingChange?.(isMutating);
    }, [isMutating, onPendingChange]);

    const defaultValues = useMemo(
      () => ({
        frequency: data?.insuranceAmount?.frequency ?? AmountFrequency.yearly,
        newFrequency: data?.proposedSolution?.insuranceAmount?.frequency ?? AmountFrequency.yearly,
        proposedSolutionStepType: data?.proposedSolution?.step ?? "keep",
        partnerId: data?.partnerId?.toString(),
        newPartnerId: data?.proposedSolution?.partnerId?.toString() ?? undefined,
        contractAnniversaryDate: data?.contractAnniversaryDate ?? undefined,
        insuranceAmount: data?.insuranceAmount?.value.toString() ?? ("0" as any),
        newInsuranceAmount: data?.proposedSolution?.insuranceAmount?.value.toString() ?? ("0" as any),
        propertyInsuranceCurentAmount: data?.propertyInsuranceCoverage.currentAmount?.value.toString() ?? ("0" as any),
        propertyInsuranceRecommendedAmount: data?.propertyInsuranceCoverage.recommendedAmount?.value.toString() ?? ("0" as any),
        householdInsuranceCurentAmount: data?.householdInsuranceCoverage.currentAmount?.value.toString() ?? ("0" as any),
        householdInsuranceRecommendedAmount: data?.householdInsuranceCoverage.recommendedAmount?.value.toString() ?? ("0" as any),
        adjacentBuildingsInsuranceCurentAmount: data?.outbuildingCoverage.currentAmount?.value.toString() ?? ("0" as any),
        adjacentBuildingsInsuranceRecommendedAmount: data?.outbuildingCoverage.recommendedAmount?.value.toString() ?? ("0" as any),
        ownerLiabilityInsuranceCurentAmount: data?.ownerLiabilityCoverage.currentAmount?.value.toString() ?? ("0" as any),
        ownerLiabilityInsuranceRecommendedAmount: data?.ownerLiabilityCoverage.recommendedAmount?.value.toString() ?? ("0" as any),
        liabilityInsuranceCurentAmount: data?.liabilityCoverage.currentAmount?.value.toString() ?? ("0" as any),
        liabilityInsuranceRecommendedAmount: data?.liabilityCoverage.recommendedAmount?.value.toString() ?? ("0" as any),
        customAmountCoverages: data?.customAmountCoverages.map((ca) => ({
          name: ca.name,
          currentAmount: ca.currentAmount?.value.toString() ?? ("0" as any),
          recommendedAmount: ca.recommendedAmount?.value.toString() ?? ("0" as any),
        })) as any,
        note: data?.note ?? undefined,
      }),
      [data],
    );

    const methods = useForm<ImmovableContractFormType>({
      resolver: yupResolver(getImmovableContractFormSchema(t)),
      mode: "onBlur",
      defaultValues,
    });

    const {
      control,
      handleSubmit,
      watch,
      reset,
      setValue,
      formState: { errors },
    } = methods;

    const { fields, append, remove } = useFieldArray({
      control,
      name: "customAmountCoverages",
    });

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        if (!!assetContractId) {
          handleSubmit(onEdit)();
        } else {
          handleSubmit(onCreate)();
        }
      },
      delete: onDelete,
    }));

    const insurancePartners: Option[] =
      insurancePartnersData?.map((partner) => ({ value: partner.id.toString(), name: partner.name })) || [];

    const frequencies: Option[] = [
      { value: AmountFrequency.one_time, name: t(Resources.Common.OneTime) },
      { value: AmountFrequency.monthly, name: t(Resources.Common.Monthly) },
      { value: AmountFrequency.quarterly, name: t(Resources.Common.Quarterly) },
      { value: AmountFrequency.half_yearly, name: t(Resources.Common.HalfYearly) },
      { value: AmountFrequency.yearly, name: t(Resources.Common.Yearly) },
    ];

    const proposedSolutionStepTypes: Option[] = [
      { value: "keep", name: t(Resources.AnalysisDetail.AssetsAndContracts.ProposedSolutionStepTypes.Keep) },
      { value: "create_recommended", name: t(Resources.AnalysisDetail.AssetsAndContracts.ProposedSolutionStepTypes.CreateRecommended) },
      { value: "modify", name: t(Resources.AnalysisDetail.AssetsAndContracts.ProposedSolutionStepTypes.Modify) },
    ];

    const buildAmount = (value: number | undefined, frequency: string) => ({
      value: value || 0,
      currencyCode: "CZK",
      frequency: frequency as AmountFrequency,
    });

    const buildBody = (data: ImmovableContractFormType) => ({
      partnerId: Number(data.partnerId),
      contractAnniversaryDate: data.contractAnniversaryDate || undefined,
      insuranceAmount: buildAmount(data.insuranceAmount, data.frequency),
      proposedSolution: {
        step: data.proposedSolutionStepType as ProposedSolutionStep,
        partnerId: data.newPartnerId ? Number(data.newPartnerId) : undefined,
        insuranceAmount: buildAmount(data.newInsuranceAmount, data.newFrequency),
      },
      propertyInsuranceCoverage: {
        currentAmount: buildAmount(data.propertyInsuranceCurentAmount, data.frequency),
        recommendedAmount: buildAmount(data.propertyInsuranceRecommendedAmount, data.frequency),
      },
      householdInsuranceCoverage: {
        currentAmount: buildAmount(data.householdInsuranceCurentAmount, data.frequency),
        recommendedAmount: buildAmount(data.householdInsuranceRecommendedAmount, data.frequency),
      },
      outbuildingCoverage: {
        currentAmount: buildAmount(data.adjacentBuildingsInsuranceCurentAmount, data.frequency),
        recommendedAmount: buildAmount(data.adjacentBuildingsInsuranceRecommendedAmount, data.frequency),
      },
      ownerLiabilityCoverage: {
        currentAmount: buildAmount(data.ownerLiabilityInsuranceCurentAmount, data.frequency),
        recommendedAmount: buildAmount(data.ownerLiabilityInsuranceRecommendedAmount, data.frequency),
      },
      liabilityCoverage: {
        currentAmount: buildAmount(data.liabilityInsuranceCurentAmount, data.frequency),
        recommendedAmount: buildAmount(data.liabilityInsuranceRecommendedAmount, data.frequency),
      },
      customAmountCoverages: data.customAmountCoverages.map((ca) => ({
        name: ca.name,
        currentAmount: buildAmount(ca.currentAmount, data.frequency),
        recommendedAmount: buildAmount(ca.recommendedAmount, data.frequency),
      })),
      note: data.note || "",
    });

    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: getListImmovableAssetContractsQueryKey(analysisId, versionId, assetItemId) });
    };

    const onCreate = async (data: ImmovableContractFormType) => {
      try {
        await createAsync({ analysisId, versionId, itemId: assetItemId, data: buildBody(data) });

        onClose();
        toast.success(t(Resources.AnalysisDetail.AssetsAndContracts.Form.ContractCreateSuccess));
        invalidate();
      } catch {
        toast.error(t(Resources.Common.Error));
      }
    };

    const onEdit = async (data: ImmovableContractFormType) => {
      try {
        await editAsync({ analysisId, versionId, itemId: assetItemId, contractId: assetContractId || "", data: buildBody(data) });

        onClose();
        toast.success(t(Resources.AnalysisDetail.AssetsAndContracts.Form.ContractEditSuccess));
        invalidate();
      } catch {
        toast.error(t(Resources.Common.Error));
      }
    };

    const onDelete = async () => {
      try {
        await deleteAsync({ analysisId, versionId, itemId: assetItemId, contractId: assetContractId || "" });

        invalidate();
        toast.success(t(Resources.AnalysisDetail.AssetsAndContracts.Form.ContractDeleteSuccess));
        onClose();
      } catch {
        toast.error(t(Resources.Common.Error));
      }
    };

    useEffect(() => {
      if (data) {
        reset(defaultValues);
      }
    }, [data, reset, defaultValues]);

    const partnerId = watch("partnerId");
    const insuranceAmount = watch("insuranceAmount");
    const frequency = watch("frequency");
    const propertyInsuranceCurentAmount = watch("propertyInsuranceCurentAmount");
    const propertyInsuranceRecommendedAmount = watch("propertyInsuranceRecommendedAmount");
    const householdInsuranceCurentAmount = watch("householdInsuranceCurentAmount");
    const householdInsuranceRecommendedAmount = watch("householdInsuranceRecommendedAmount");
    const adjacentBuildingsInsuranceCurentAmount = watch("adjacentBuildingsInsuranceCurentAmount");
    const adjacentBuildingsInsuranceRecommendedAmount = watch("adjacentBuildingsInsuranceRecommendedAmount");
    const ownerLiabilityInsuranceCurentAmount = watch("ownerLiabilityInsuranceCurentAmount");
    const ownerLiabilityInsuranceRecommendedAmount = watch("ownerLiabilityInsuranceRecommendedAmount");
    const liabilityInsuranceCurentAmount = watch("liabilityInsuranceCurentAmount");
    const liabilityInsuranceRecommendedAmount = watch("liabilityInsuranceRecommendedAmount");
    const customAmountCoverages = watch("customAmountCoverages");
    const proposedSolutionStepType = watch("proposedSolutionStepType");

    const propertyUnderinsurance = calculateUnderinsurance(propertyInsuranceCurentAmount, propertyInsuranceRecommendedAmount)?.toString();
    const householdUnderinsurance = calculateUnderinsurance(
      householdInsuranceCurentAmount,
      householdInsuranceRecommendedAmount,
    )?.toString();
    const adjacentBuildingsUnderinsurance = calculateUnderinsurance(
      adjacentBuildingsInsuranceCurentAmount,
      adjacentBuildingsInsuranceRecommendedAmount,
    )?.toString();
    const ownerLiabilityUnderinsurance = calculateUnderinsurance(
      ownerLiabilityInsuranceCurentAmount,
      ownerLiabilityInsuranceRecommendedAmount,
    )?.toString();
    const liabilityUnderinsurance = calculateUnderinsurance(
      liabilityInsuranceCurentAmount,
      liabilityInsuranceRecommendedAmount,
    )?.toString();

    return (
      <FormProvider {...methods}>
        <form>
          <Box>
            <Stack spacing={Spacing.XXL}>
              <Grid container spacing={Spacing.XL}>
                {expenseOptions && expenseOptions.length > 0 && (
                  <Grid size={6}>
                    <Popper
                      placement="bottom-start"
                      isOpen={expenseOptionsDropdownOpen}
                      onOpen={() => {
                        setExpenseOptionsDropdownOpen(true);
                      }}
                      btnEl={
                        <Button
                          variant="text"
                          disabled={readOnly}
                          colorCustom={theme.colors.brandPrimary}
                          sx={{ width: "fit-content" }}
                          endAdornment={<ArrowDownIcon />}
                        >
                          {t(Resources.AnalysisDetail.AssetsAndContracts.Form.LoadFromExistingExpenses)}
                        </Button>
                      }
                    >
                      <Stack spacing={Spacing.M} paddingY={Spacing.S} display="flex" justifyContent="space-between">
                        {expenseOptions.map((option, index) => (
                          <StyledItem
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "space-between",
                              alignItems: "center",
                              paddingX: Spacing.M,
                              paddingY: Spacing.S,
                              borderRadius: Radius.S,
                            }}
                            key={`${option.expenseType}-${index}`}
                            onClick={() => {
                              setValue("insuranceAmount", option.amount?.value.toString() as any);
                              if (option.amount?.frequency) setValue("frequency", option.amount.frequency);
                              setExpenseOptionsDropdownOpen(false);
                            }}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {t(
                                  Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.InsuranceExpenseTypes[
                                    LOAD_VALUES_EXPENSE_TYPE_RESOURCE_KEY[option.expenseType]
                                  ],
                                )}
                              </Typography>
                              <Typography variant="caption" color="secondary" fontWeight={500} mb={Spacing.M}>
                                {option.institution}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="secondary" fontWeight={500}>
                              {formatCurrency(option.amount?.value, option.amount?.currencyCode)} /{" "}
                              {option.amount?.frequency ? getAmountFrequencyTranslation(option.amount.frequency, t) : ""}
                            </Typography>
                          </StyledItem>
                        ))}
                      </Stack>
                    </Popper>
                  </Grid>
                )}

                <Grid size={8}>
                  <FormDropdown
                    name="partnerId"
                    options={insurancePartners}
                    control={control}
                    disabled={readOnly}
                    label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Partner)}
                    labelEndAdornment={<StyledLabelAdornment />}
                    errors={errors}
                  />
                </Grid>
                <Grid size={4}>
                  <DatePicker
                    control={control}
                    disabled={readOnly}
                    name="contractAnniversaryDate"
                    label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.ContractAnniversaryDate)}
                    errors={errors}
                  />
                </Grid>
                <Grid size={6}>
                  <BlFormInput
                    disabled={readOnly}
                    control={control}
                    name="insuranceAmount"
                    label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.InsuranceAmount)}
                    errors={errors}
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 0,
                        min: 0,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        Kč
                      </Typography>
                    }
                  />
                </Grid>
                <Grid size={6}>
                  <FormDropdown
                    name="frequency"
                    options={frequencies}
                    control={control}
                    disabled={readOnly}
                    label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Frequency)}
                    errors={errors}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={Spacing.XL}>
                <Grid size={12}>
                  <Typography variant="body1" fontWeight={600}>
                    {t(Resources.AnalysisDetail.AssetsAndContracts.Form.CoverageAmount)}
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="body2" color="secondary" fontWeight={500}>
                    {t(Resources.AnalysisDetail.AssetsAndContracts.Form.CoverageType)}
                  </Typography>
                </Grid>
                <Grid size={3}>
                  <Typography variant="body2" fontWeight={500}>
                    {t(Resources.AnalysisDetail.AssetsAndContracts.Form.CurrentAmount)}
                  </Typography>
                </Grid>
                <Grid size={3}>
                  <Typography variant="body2" fontWeight={500}>
                    {t(Resources.AnalysisDetail.AssetsAndContracts.Form.RecommendedAmount)}
                  </Typography>
                </Grid>
                <Grid size={2}>
                  <Typography variant="body2" fontWeight={500}>
                    {t(Resources.AnalysisDetail.AssetsAndContracts.Form.Underinsurance)}
                  </Typography>
                </Grid>
                <Grid size={4} display="flex" alignItems="center">
                  <TextInput disabled value={t(Resources.AnalysisDetail.AssetsAndContracts.Form.PropertyInsuranceCoverage)} />
                </Grid>
                <Grid size={3}>
                  <BlFormInput
                    disabled={readOnly}
                    control={control}
                    name="propertyInsuranceCurentAmount"
                    errors={errors}
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 0,
                        min: 0,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        Kč
                      </Typography>
                    }
                  />
                </Grid>
                <Grid size={3}>
                  <BlFormInput
                    disabled={readOnly}
                    control={control}
                    name="propertyInsuranceRecommendedAmount"
                    errors={errors}
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 0,
                        min: 0,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        Kč
                      </Typography>
                    }
                  />
                </Grid>
                <Grid size={2}>
                  <TextInput
                    disabled
                    value={propertyUnderinsurance}
                    error={propertyUnderinsurance ? Number(propertyUnderinsurance) < 0 : undefined}
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        %
                      </Typography>
                    }
                  />
                </Grid>
                {/* ------ */}
                <Grid size={4} display="flex" alignItems="center">
                  <TextInput disabled value={t(Resources.AnalysisDetail.AssetsAndContracts.Form.HouseholdInsuranceCoverage)} />
                </Grid>
                <Grid size={3}>
                  <BlFormInput
                    disabled={readOnly}
                    control={control}
                    name="householdInsuranceCurentAmount"
                    errors={errors}
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 0,
                        min: 0,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        Kč
                      </Typography>
                    }
                  />
                </Grid>
                <Grid size={3}>
                  <BlFormInput
                    disabled={readOnly}
                    control={control}
                    name="householdInsuranceRecommendedAmount"
                    errors={errors}
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 0,
                        min: 0,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        Kč
                      </Typography>
                    }
                  />
                </Grid>
                <Grid size={2}>
                  <TextInput
                    value={householdUnderinsurance}
                    error={householdUnderinsurance ? Number(householdUnderinsurance) < 0 : undefined}
                    disabled
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        %
                      </Typography>
                    }
                  />
                </Grid>
                {/* ------ */}
                <Grid size={4} display="flex" alignItems="center">
                  <TextInput disabled value={t(Resources.AnalysisDetail.AssetsAndContracts.Form.OutbuildingCoverage)} />
                </Grid>
                <Grid size={3}>
                  <BlFormInput
                    disabled={readOnly}
                    control={control}
                    name="adjacentBuildingsInsuranceCurentAmount"
                    errors={errors}
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 0,
                        min: 0,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        Kč
                      </Typography>
                    }
                  />
                </Grid>
                <Grid size={3}>
                  <BlFormInput
                    disabled={readOnly}
                    control={control}
                    name="adjacentBuildingsInsuranceRecommendedAmount"
                    errors={errors}
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 0,
                        min: 0,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        Kč
                      </Typography>
                    }
                  />
                </Grid>
                <Grid size={2}>
                  <TextInput
                    value={adjacentBuildingsUnderinsurance}
                    error={adjacentBuildingsUnderinsurance ? Number(adjacentBuildingsUnderinsurance) < 0 : undefined}
                    disabled
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        %
                      </Typography>
                    }
                  />
                </Grid>
                {/* ------ */}
                <Grid size={4} display="flex" alignItems="center">
                  <TextInput disabled value={t(Resources.AnalysisDetail.AssetsAndContracts.Form.OwnerLiabilityCoverage)} />
                </Grid>
                <Grid size={3}>
                  <BlFormInput
                    disabled={readOnly}
                    control={control}
                    name="ownerLiabilityInsuranceCurentAmount"
                    errors={errors}
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 0,
                        min: 0,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        Kč
                      </Typography>
                    }
                  />
                </Grid>
                <Grid size={3}>
                  <BlFormInput
                    disabled={readOnly}
                    control={control}
                    name="ownerLiabilityInsuranceRecommendedAmount"
                    errors={errors}
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 0,
                        min: 0,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        Kč
                      </Typography>
                    }
                  />
                </Grid>
                <Grid size={2}>
                  <TextInput
                    value={ownerLiabilityUnderinsurance}
                    error={ownerLiabilityUnderinsurance ? Number(ownerLiabilityUnderinsurance) < 0 : undefined}
                    disabled
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        %
                      </Typography>
                    }
                  />
                </Grid>
                {/* ------ */}
                <Grid size={4} display="flex" alignItems="center">
                  <TextInput disabled value={t(Resources.AnalysisDetail.AssetsAndContracts.Form.LiabilityCoverage)} />
                </Grid>
                <Grid size={3}>
                  <BlFormInput
                    disabled={readOnly}
                    control={control}
                    name="liabilityInsuranceCurentAmount"
                    errors={errors}
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 0,
                        min: 0,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        Kč
                      </Typography>
                    }
                  />
                </Grid>
                <Grid size={3}>
                  <BlFormInput
                    disabled={readOnly}
                    control={control}
                    name="liabilityInsuranceRecommendedAmount"
                    errors={errors}
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 0,
                        min: 0,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        Kč
                      </Typography>
                    }
                  />
                </Grid>
                <Grid size={2}>
                  <TextInput
                    value={liabilityUnderinsurance}
                    error={liabilityUnderinsurance ? Number(liabilityUnderinsurance) < 0 : undefined}
                    disabled
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                      },
                    ]}
                    inputEndAdornment={
                      <Typography variant="body2" color="secondary">
                        %
                      </Typography>
                    }
                  />
                </Grid>
                {fields.map((field, index) => {
                  const row = customAmountCoverages?.[index];
                  const underinsurance = calculateUnderinsurance(row?.currentAmount, row?.recommendedAmount)?.toString();

                  return (
                    <React.Fragment key={field.id}>
                      <Grid size={3}>
                        <BlFormInput name={`customAmountCoverages.${index}.name`} disabled={readOnly} control={control} errors={errors} />
                      </Grid>
                      <Grid size={3}>
                        <BlFormInput
                          disabled={readOnly}
                          control={control}
                          name={`customAmountCoverages.${index}.currentAmount`}
                          errors={errors}
                          mask={[
                            {
                              mask: Number,
                              thousandsSeparator: " ",
                              scale: 0,
                              min: 0,
                            },
                          ]}
                          inputEndAdornment={
                            <Typography variant="body2" color="secondary">
                              Kč
                            </Typography>
                          }
                        />
                      </Grid>
                      <Grid size={3}>
                        <BlFormInput
                          disabled={readOnly}
                          control={control}
                          name={`customAmountCoverages.${index}.recommendedAmount`}
                          errors={errors}
                          mask={[
                            {
                              mask: Number,
                              thousandsSeparator: " ",
                              scale: 0,
                              min: 0,
                            },
                          ]}
                          inputEndAdornment={
                            <Typography variant="body2" color="secondary">
                              Kč
                            </Typography>
                          }
                        />
                      </Grid>
                      <Grid size={2}>
                        <TextInput
                          value={underinsurance}
                          error={underinsurance ? Number(underinsurance) < 0 : undefined}
                          disabled
                          mask={[
                            {
                              mask: Number,
                              thousandsSeparator: " ",
                              scale: 2,
                            },
                          ]}
                          inputEndAdornment={
                            <Typography variant="body2" color="secondary">
                              %
                            </Typography>
                          }
                        />
                      </Grid>
                      <Grid size={1} display="flex" alignItems="center" justifyContent="center">
                        <Button
                          variant="text"
                          disabled={readOnly}
                          sx={{ minWidth: "auto", padding: Spacing.S }}
                          onClick={() => remove(index)}
                        >
                          <BinIcon />
                        </Button>
                      </Grid>
                    </React.Fragment>
                  );
                })}
                <Grid size={12}>
                  <Button
                    variant="text"
                    disabled={readOnly}
                    colorCustom={theme.colors.brandPrimary}
                    sx={{ width: "fit-content", marginLeft: -Spacing.L }}
                    startAdornment={<AddIcon />}
                    onClick={() =>
                      append({
                        name: "",
                        currentAmount: Number(0).toString() as any,
                        recommendedAmount: Number(0).toString() as any,
                      })
                    }
                  >
                    {t(Resources.AnalysisDetail.AssetsAndContracts.Form.AddCoverage)}
                  </Button>
                </Grid>
              </Grid>
              <Divider />
              <Grid container spacing={Spacing.XL}>
                <Grid size={12}>
                  <Typography fontWeight={600} variant="subtitle1">
                    {t(Resources.AnalysisDetail.AssetsAndContracts.Form.ProposedSolutionSteps)}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={Spacing.XL}>
                <Grid size={6}>
                  <FormDropdown
                    name="proposedSolutionStepType"
                    options={proposedSolutionStepTypes}
                    control={control}
                    disabled={readOnly}
                    label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.ProposedSolution)}
                    errors={errors}
                    onChange={(value) => {
                      if (value === "modify") {
                        if (partnerId) setValue("newPartnerId", partnerId);
                        if (insuranceAmount) setValue("newInsuranceAmount", insuranceAmount);
                        if (frequency) setValue("newFrequency", frequency);
                      }
                    }}
                  />
                </Grid>
              </Grid>
              {proposedSolutionStepType !== "keep" && (
                <Grid container spacing={Spacing.XL}>
                  <Grid size={6}>
                    <FormDropdown
                      name="newPartnerId"
                      options={insurancePartners}
                      control={control}
                      disabled={readOnly}
                      label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.NewPartner)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                    />
                  </Grid>
                  <Grid size={3}>
                    <BlFormInput
                      disabled={readOnly}
                      control={control}
                      name="newInsuranceAmount"
                      label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.InsuranceAmount)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                      mask={[
                        {
                          mask: Number,
                          thousandsSeparator: " ",
                          scale: 0,
                          min: 0,
                        },
                      ]}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          Kč
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormDropdown
                      name="newFrequency"
                      options={frequencies}
                      control={control}
                      disabled={readOnly}
                      label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Frequency)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                    />
                  </Grid>
                </Grid>
              )}

              <Grid size={12}>
                <BlFormInput
                  control={control}
                  label={t(Resources.Common.Note)}
                  name="note"
                  errors={errors}
                  disabled={readOnly}
                  multiline
                  minRows={6}
                  maxRows={8}
                  characterCounter={<FormCharacterCounter characterLimit={NOTE_MAX_LENGTH} name={"note"} />}
                />
              </Grid>
            </Stack>
          </Box>
        </form>
      </FormProvider>
    );
  },
);
